<script setup lang="ts">
/**
 * One card of the assessment slider (IPD-3052).
 *
 * Presentational only: it draws the state it is handed. Which single card is open, and in what order
 * the rest follow, is entirely the backend's (IPD-2971) — the frontend obeys.
 *
 * A locked card is shown, not hidden. It is the next slide of the slider, so the user can see what is
 * coming; it simply carries no call to action, and is drawn in grey.
 */
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import TodayAssessmentArt from '@/vue/molecules/today/today-assessment-art.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {
  type TodayAssessmentSlide,
  TodayAssessmentSlideState,
} from '@/ts/types/component/today-page';
import {
  resolveTodayUnlockCountdown,
  TodayCountdownUnit,
} from '@/ts/composables/pure/use-today-unlock-countdown';
import {readableTint} from '@/ts/composables/pure/use-readable-tint';
import {useLogger} from '@/ts/composables/pure/use-logger';

const props = defineProps<{
  slide: TodayAssessmentSlide;
  /**
   * Whether this is the card the slider is showing. The artwork plays when a card arrives as well as
   * when the pointer enters it: arriving under a pointer that has not moved fires no enter event, so
   * paging through with the arrows would otherwise leave every card after the first sitting still.
   */
  onScreen?: boolean;
  /**
   * Whether the pointer is in the tile. Owned by the tile, not by this card: the paging arrows sit
   * over the card without being inside it, so a card that watched its own pointer read a trip to the
   * arrow as a departure and stopped mid-animation.
   */
  pointedAt?: boolean;
  /**
   * The slider already says, above the cards, that nothing here can be answered yet. The padlock is
   * then left off: it would repeat that sentence once per card, and the two land in the same corner.
   */
  lockAnnounced?: boolean;
}>();

const emit = defineEmits<{
  /** The user wants to answer this one. Only ever fired by the open card. */
  select: [slide: TodayAssessmentSlide];
}>();

const {t: $t} = useI18n();

// The card is the whole tile: one narrow column of the bento, around 426px where the design draws
// it, and the whole row at the width the no-holes rule stretches it to. lazy-image scales it by the
// device pixel ratio on top.
const IMAGE_WIDTH_PX = 512;

/**
 * The design gives every assessment a colour of its own, and the CMS now carries it — the five the
 * design system draws, one per assessment. Where it does not, the colour comes from the
 * questionnaire's id instead: the same assessment keeps the same card wherever it lands in the
 * queue, and no two reads of the page disagree. Deliberately NOT the position, which would repaint
 * the whole deck as soon as one is answered and the queue shifts up.
 */
const TINTS = 5;

const COUNTDOWN_KEY: Record<TodayCountdownUnit, string> = {
  [TodayCountdownUnit.days]: 'today_assessment_unlocks_d',
  [TodayCountdownUnit.hours]: 'today_assessment_unlocks_h',
  [TodayCountdownUnit.minutes]: 'today_assessment_unlocks_m',
};

const isOpen = computed<boolean>(() => props.slide.state === TodayAssessmentSlideState.open);

const showLock = computed<boolean>(() => !isOpen.value && !props.lockAnnounced);

/**
 * Arriving is counted, not held: making "on screen" a reason to keep playing left the visible card
 * permanently playing, so entering it with the pointer changed nothing and the animation could never
 * be triggered again.
 */
const arrivals = ref(0);
watch(() => props.onScreen, (onScreen) => {
  if (onScreen) {
    arrivals.value++;
  }
}, {immediate: true});

/**
 * The animated drawing failed to arrive, so the card goes back to the questionnaire's own picture.
 * Worth its own flag rather than a silent empty box: the drawing lives in the CMS and travels over a
 * permission that can be missing on a given environment, and neither is a reason to show nothing.
 */
const artUnavailable = ref(false);
watch(() => props.slide.artUrl, () => {
  artUnavailable.value = false;
});

/**
 * Handed to CSS as a variable rather than through a class, because a colour chosen in a CMS cannot
 * be enumerated in a stylesheet. The tint classes stay for everything the CMS has no colour for —
 * and for everything the ink cannot be read on, since the card's text is pinned to one colour and a
 * dark enough choice in Directus would otherwise ship an unreadable card.
 */
const logger = useLogger();

const cmsTint = computed<string | null>(() => {
  const chosen = props.slide.backgroundColor;
  const readable = readableTint(chosen);
  if ((chosen ?? null) !== null && readable === null) {
    logger.warn(`Ignoring the CMS colour ${chosen} for "${props.slide.title}": the card's text would not be readable on it`)();
  }
  return readable;
});

const tint = computed<number>(() => {
  // A sum of character codes is enough to spread ids that differ anywhere, and it cannot throw on an
  // id shape the backend changes its mind about later.
  let sum = 0;
  for (let index = 0; index < props.slide.id.length; index++) {
    sum += props.slide.id.charCodeAt(index);
  }
  return sum % TINTS;
});

const countdown = computed(() => resolveTodayUnlockCountdown(props.slide.unlockInMillis));

/**
 * Pluralised through the count, the way the app's other countdown does it, so "1 day" and "2 days"
 * both come from one key and German gets its own plural rule rather than an English one.
 */
const countdownLabel = computed<string | null>(() => {
  const left = countdown.value;
  return left === null
    ? null
    : $t(COUNTDOWN_KEY[left.unit], left.value, {named: {count: left.value}});
});

/**
 * The card's name for assistive technology. The kind comes first because the visible eyebrow does,
 * and the countdown is appended after the title rather than woven between the visible lines: a name
 * has to contain the visible text in order (WCAG 2.5.3) or voice control cannot act on what it reads.
 */
const ariaLabel = computed<string>(() => [
  $t('assessment'),
  props.slide.title,
  countdownLabel.value,
  // Grey is the visible signal and the padlock is decorative, so without this a queued card reaches
  // a screen reader as an ordinary card (1.4.1). Only where there is no countdown: "Unlocks in 3
  // days" already says it, and a card is either counting down or queued, never both. Last, so the
  // visible text still comes through in the order it is drawn (2.5.3).
  isOpen.value || countdownLabel.value !== null ? null : $t('today_assessment_locked'),
].filter(Boolean).join(', '));
</script>

<template>
  <article
    class="assessment-card"
    :class="[`assessment-card--${slide.state}`, `assessment-card--tint-${tint}`]"
    :style="cmsTint ? {'--card-tint': cmsTint} : undefined"
    :aria-label="ariaLabel"
  >
    <!-- The animated illustration the CMS holds for this questionnaire, and its picture wherever it
         holds none — the same asset the current home page shows. Either is centred on the card's
         colour rather than covering it, because the design's artwork is a transparent drawing
         sitting on that colour and the copy below has to keep its contrast whatever the CMS serves.
         A questionnaire with neither keeps a glyph rather than a blank card. -->
    <div class="assessment-card__art" aria-hidden="true">
      <today-assessment-art
        v-if="slide.artUrl && !artUnavailable"
        :src="slide.artUrl"
        :playing="Boolean(pointedAt) && Boolean(onScreen)"
        :on-screen="Boolean(onScreen)"
        :arrivals="arrivals"
        @unavailable="artUnavailable = true"
      />
      <lazy-image
        v-else-if="slide.imageUrl"
        :src="slide.imageUrl"
        :width="IMAGE_WIDTH_PX"
        skip-loader
        class="assessment-card__picture"
      />
      <q-icon v-else name="assignment" class="assessment-card__glyph"/>
    </div>

    <!-- A locked card says so in its corner as well as by dimming: the design marks it with a
         padlock on a translucent disc, the mirror of the eyebrow the open card carries on the other
         side. The dimming alone is easy to miss on a card whose artwork is pale to begin with.
         `lock.svg` is the app's own, the one a locked session already shows. -->
    <span v-if="showLock" class="assessment-card__lock" aria-hidden="true">
      <svg-illustration name="lock.svg"/>
    </span>

    <div class="assessment-card__meta">
      <div class="assessment-card__copy">
        <span class="assessment-card__eyebrow">{{ $t('assessment') }}</span>
        <!-- h2, not h3: the page's only h1 is its own title and nothing sits between the two, so a
             third level here would skip one. The tile is the first of eight to carry a real heading,
             which makes this the level its siblings should line up with. -->
        <h2 class="assessment-card__title">
          {{ slide.title }}
        </h2>
        <!-- No padlock beside it: the design draws this line as plain text, and the card already
             carries one in its corner. Two of them, in two different glyphs, said it twice. -->
        <p v-if="countdownLabel" class="assessment-card__countdown">
          {{ countdownLabel }}
        </p>
      </div>

      <!-- The design draws a bare 24px arrow. It keeps that look and takes the app's 44px target
           around it, since this is the only way into the questionnaire from here. -->
      <button
        v-if="isOpen"
        type="button"
        class="assessment-card__cta"
        :class="{'assessment-card__cta--nudged': pointedAt}"
        :aria-label="$t('today_assessment_cta')"
        @click="emit('select', slide)"
      >
        <svg-illustration name="arrow-right.svg" aria-hidden="true"/>
      </button>
    </div>
  </article>
</template>

<style scoped lang="scss">
// The card fills the tile and paints it a colour of its own, which is what the design draws: the
// artwork floats on that colour with the copy resting at the bottom of it. Both the colour and the
// ink on it are fixed across schemes — see the tokens, where the contrast that forces it is
// recorded.
.assessment-card {
  position: relative;
  display: flex;
  flex: 0 0 100%;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--Spacing-spacing-4);
  // The design's own 24 / 42 / 60, which sits the copy well clear of the rounded corners.
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-6) var(--Spacing-spacing-8);
  background-color: var(--card-tint);
  color: var(--today-assessment-ink);
}

.assessment-card--tint-0 {
  --card-tint: var(--today-assessment-tint-sky);
}

.assessment-card--tint-1 {
  --card-tint: var(--today-assessment-tint-sage);
}

.assessment-card--tint-2 {
  --card-tint: var(--today-assessment-tint-blossom);
}

.assessment-card--tint-3 {
  --card-tint: var(--today-assessment-tint-amber);
}

.assessment-card--tint-4 {
  --card-tint: var(--today-assessment-tint-lilac);
}

// The artwork takes the space the copy does not, centred in it. Capped at the same square the art
// component uses rather than fixed, so it shrinks with the tile instead of pushing the copy out of a
// short one. The cap and the prototype have been measured a month apart and disagree by 20px (283
// here, 263 in the earlier note below the tile tokens); the number wants confirming against the
// prototype before either is treated as the design's word.
.assessment-card__art {
  display: grid;
  flex: 1;
  place-items: center;
  min-height: 0;
}

// The same box as the drawing that replaces it (see today-assessment-art.vue): one stands in for the
// other when the CMS has no illustration, or when it cannot be fetched, and a fallback that resizes
// the card's contents by twenty pixels announces itself.
.assessment-card__picture {
  width: min(100%, 283px);
  height: 100%;
  object-fit: contain;
  // The design keeps the artwork alive under the pointer rather than at rest — its stated aim is a
  // page that moves when the user moves, not one that moves on its own. The hover query keeps a
  // touch device from being left with a card mid-animation it can never trigger.
  transition: transform 0.4s ease-out;

  @media (hover: hover) {
    .assessment-card:hover & {
      transform: scale(1.06) translateY(-4px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    .assessment-card:hover & {
      transform: none;
    }
  }
}

// The stand-in while design has no artwork per assessment: a glyph in the ink of the card, held
// back so it reads as a placeholder rather than as the picture itself.
.assessment-card__glyph {
  font-size: 96px;
  opacity: 0.35;
}

// A locked card drops its colour and dims its artwork (IPD-3131). `background-color` rather than
// `--card-tint`, so a CMS colour — an inline custom property — greys out with the rest. Keyed on
// "not open" rather than on the two locked states, which is what the script already means by locked.
.assessment-card:not(.assessment-card--open) {
  background-color: var(--today-assessment-tint-locked);
}

.assessment-card:not(.assessment-card--open) .assessment-card__art {
  filter: grayscale(1);
  opacity: 0.45;
}

// A 40px disc of the card's own light holding an 18px padlock, measured on the prototype along with
// its position. Translucent rather than solid, so the artwork stays visible through it and the mark
// reads as laid over the card rather than punched into it.
.assessment-card__lock {
  position: absolute;
  // Square in its corner, and the same step the tile's own lead line uses on the face that carries
  // the mark instead of the cards — so the padlock is at one height whichever face is on screen.
  top: var(--Spacing-spacing-6);
  left: var(--Spacing-spacing-6);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--today-assessment-scrim) 70%, transparent);
  color: var(--today-assessment-ink);

  :deep(svg) {
    width: 18px;
    height: 18px;
  }
}

.assessment-card__meta {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--Spacing-spacing-4);
}

// Literals, for the same reason the shared card uses them (today-item-card.vue): the repo's spacing
// scale steps straight from 0 to 8px on desktop, and the design sets these three lines 3px apart.
// The scale's smallest step resolves to 8 on a wide window, which pushed the block to twice the
// separation the design draws — and doubly so under the countdown, which added a margin of its own
// on top of this gap.
.assessment-card__copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.assessment-card__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-eyebrow);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

// The design sets the title in the display serif at its lightest weight, not in the heading face.
// The tracking is given as a ratio rather than the design's -0.75px, so it holds at the size the
// app's own scale produces when the user has raised the minimum font size.
.assessment-card__title {
  @include font-display-4;

  margin: 0;
  font-size: var(--today-tile-title-size);
  letter-spacing: -0.025em;
  // Questionnaire names come from the CMS, and German compounds can be longer than the card is wide.
  overflow-wrap: anywhere;
}

// Set apart from the title above it by weight and by strength, not by size: the design draws it in
// the body face at its medium weight and holds it back, where this was bold and at full strength
// and read as a second title. The flex row went with the padlock it used to hold.
//
// Held back to 0.75 rather than the design's 0.55. At 0.55 the ink composites to about 2.8:1 on the
// darkest of the five tints, and this is normal-size body text on a card whose colours are pinned
// across schemes, so nothing else recovers it. 0.75 keeps the line clearly quieter than the title
// while landing near 4.5:1. Raised with design along with the other Today notes.
.assessment-card__countdown {
  @include font-body-sm;

  // On top of the gap above, which is what the design does too: the line sits a step further from
  // the title than the title from the eyebrow, so the pair of names reads together and the state
  // reads apart from them.
  margin: 3px 0 0;
  opacity: 0.75;
}

// On the locked grey, 0.75 composites to 4.34:1 — under 1.4.3's 4.5 for body text. 0.85 gives 5.48:1.
// Only the cooldown card: `resolveTodayAssessments` gives every queued slide a null countdown.
.assessment-card--cooldown .assessment-card__countdown {
  opacity: 0.85;
}

.assessment-card__cta {
  @include focus-visible;

  // Over the card's tint, which does not flip with the scheme, the app's focus colour measures
  // 2.25 to 2.94:1 across the five — under the 3:1 an indicator has to reach (1.4.11). The ink the
  // card is already drawn in gives 7.42 to 9.72:1, and 7.45:1 on the locked grey (IPD-3131).
  &:focus-visible {
    outline-color: var(--today-assessment-ink);
  }

  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 44px;
  height: 44px;
  // The arrow is drawn at the card's edge, so the target grows inwards instead of hanging off it.
  margin-right: calc(var(--Spacing-spacing-2) * -1);
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: transform 0.2s ease;

  :deep(svg) {
    width: 24px;
    height: 24px;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateX(4px);
    }
  }

  // Nudged while the pointer is anywhere in the tile, not only on the arrow itself, which is what
  // the design's prototype does: the card is one large target and this says where the press goes.
  //
  // Driven by the tile's own `pointedAt` rather than by a hover of its own, so it answers to the
  // same thing the artwork does — including its two hard-won rules, that the paging arrows sit over
  // the card without being inside it, and that a focus the mouse left behind does not count.
  // Behind the same pointer query as its neighbours. `pointedAt` comes from the tile's mouseenter,
  // which a touch browser synthesises on a tap, and without this a tap would leave the arrow
  // leaning until something else was tapped — the state the artwork's own guard exists to prevent.
  @media (hover: hover) {
    &--nudged {
      transform: translateX(4px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &--nudged {
      transform: none;
    }
  }
}
</style>
